import React, { useState, useEffect, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn, ColumnSelector, type ColumnDefinition, useColumnVisibility,
} from 'components/ui';
import { EnterpriseScreenShell, actionButtonFactory } from 'components/enterprise-shell';
import notificationService from 'services/notificationService';
import { accountTypeService, reportClassService, accountSubtypeService } from 'api/services';
import type { AccountSubtypeDto, AccountTypeDto, ReportClassDto } from 'api/types';
import { DetailList } from 'components/modals/modules/settings/SettingsModalFrame';
import { statementLabel, humanize, appliesTo } from './AccountTypesScreen';

// ============================================================================
// ACCOUNT SUBTYPES (Settings) — the subtypes ledgers and groups are classified
// under, with their account type, report class, statement, section and normal
// balance. Product reference data shared by every entity; read-only here.
// House list screen: search, list / card view, filters, column selector, View.
// ============================================================================

type ViewMode = 'list' | 'grid';

const COLUMN_DEFS: ColumnDefinition[] = [
  { id: 'subtypeCode', label: 'Code' },
  { id: 'subtypeName', label: 'Subtype' },
  { id: 'type', label: 'Account type' },
  { id: 'reportClass', label: 'Report class' },
  { id: 'statement', label: 'Statement' },
  { id: 'section', label: 'Section' },
  { id: 'normalBalance', label: 'Normal balance' },
  { id: 'appliesTo', label: 'Applies to' },
  { id: 'actions', label: 'Actions', required: true },
];

const balance = (b: string) => (b === 'DR' ? 'Debit' : b === 'CR' ? 'Credit' : b);

interface Props { moduleIcon?: string; tabId?: string }

const AccountSubtypesScreen: React.FC<Props> = ({ moduleIcon, tabId }) => {
  const [rows, setRows] = useState<AccountSubtypeDto[]>([]);
  const [types, setTypes] = useState<AccountTypeDto[]>([]);
  const [classes, setClasses] = useState<ReportClassDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const { visibleColumns, onApply: applyColumns, onReset: resetColumns } = useColumnVisibility('account-subtypes-columns', COLUMN_DEFS);
  const [filterOpen, setFilterOpen] = useState(false);
  const [draft, setDraft] = useState({ typeId: '', classId: '', statement: '', allCountries: false });
  const [applied, setApplied] = useState({ typeId: '', classId: '', statement: '', allCountries: false });
  const [viewing, setViewing] = useState<AccountSubtypeDto | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, c, s] = await Promise.all([
        accountTypeService.getAll({ pageSize: 100 }),
        reportClassService.getAll({ pageSize: 100 }),
        accountSubtypeService.getAll({ pageSize: 100, allCountries: applied.allCountries }),
      ]);
      setTypes(t.data); setClasses(c.data); setRows(s.data);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not load the subtypes.');
    } finally {
      setLoading(false);
    }
  }, [applied.allCountries]);

  useEffect(() => { load(); }, [load]);

  const typeOrder = useMemo(() => new Map(types.map(t => [t.id, t.displayOrder])), [types]);
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter(r => (!applied.typeId || r.accountTypeId === applied.typeId)
        && (!applied.classId || r.reportClassId === applied.classId)
        && (!applied.statement || r.reportType === applied.statement)
        && (!q || [r.subtypeCode, r.subtypeName, r.reportClassName, r.accountTypeName].some(v => v?.toLowerCase().includes(q))))
      .sort((a, b) => (typeOrder.get(a.accountTypeId) ?? 0) - (typeOrder.get(b.accountTypeId) ?? 0) || a.displayOrder - b.displayOrder);
  }, [rows, search, applied, typeOrder]);

  const rowActions = useCallback((r: AccountSubtypeDto): ActionMenuItem[] => [
    { id: 'view', label: 'View', icon: <IconSystem.eye size={18} />, onClick: () => setViewing(r) },
  ], []);

  const columns: SyutGridColumn<AccountSubtypeDto>[] = useMemo(() => [
    { id: 'subtypeCode', header: 'Code', accessorKey: 'subtypeCode', size: 210 },
    { id: 'subtypeName', header: 'Subtype', accessorKey: 'subtypeName', size: 240,
      cell: ({ row }) => (
        <button type="button" onClick={() => setViewing(row.original)}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--primary)', textAlign: 'left' }}>
          {row.original.subtypeName}
        </button>
      ) },
    { id: 'type', header: 'Account type', size: 140, cell: ({ row }) => row.original.accountTypeName },
    { id: 'reportClass', header: 'Report class', size: 200, cell: ({ row }) => row.original.reportClassName || '—' },
    { id: 'statement', header: 'Statement', size: 150, cell: ({ row }) => statementLabel(row.original.reportType) },
    { id: 'section', header: 'Section', size: 180, cell: ({ row }) => humanize(row.original.reportSection) || '—' },
    { id: 'normalBalance', header: 'Normal balance', size: 130, cell: ({ row }) => balance(row.original.normalBalance) },
    { id: 'appliesTo', header: 'Applies to', size: 200, cell: ({ row }) => appliesTo(row.original.countryCodes, row.original.legalForms) },
  ], []);

  const statements = useMemo(() => [...new Set(rows.map(r => r.reportType).filter(Boolean))], [rows]);
  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Card View' },
  ];
  const actionButtons: ActionButton[] = [actionButtonFactory.refresh(load)];
  const appliedCount = (applied.typeId ? 1 : 0) + (applied.classId ? 1 : 0) + (applied.statement ? 1 : 0) + (applied.allCountries ? 1 : 0);
  const sel: React.CSSProperties = { fontSize: '0.8125rem', padding: '0.375rem 0.625rem' };

  return (
    <EnterpriseScreenShell
      title="Account Subtypes"
      description="The subtypes ledgers and groups are classified under (shared by every entity; read-only)"
      icon={moduleIcon && (IconSystem as any)[moduleIcon]
        ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
        : <IconSystem.folder size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      searchConfig={{ value: search, onChange: setSearch, placeholder: 'Search subtypes...' }}
      viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: m => setViewMode(m as ViewMode) }}
      filterConfig={{ onClick: () => { setDraft(applied); setFilterOpen(!filterOpen); }, isActive: filterOpen, appliedCount }}
      customToolbarItems={<ColumnSelector columns={COLUMN_DEFS} visibleColumns={visibleColumns} onApply={applyColumns} onReset={resetColumns} />}
      trailingContent={filterOpen && (
        <div className="search-panel">
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters"><IconSystem.x size={14} /></button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Account type</label>
              <select className="form-input" style={sel} value={draft.typeId} onChange={e => setDraft(d => ({ ...d, typeId: e.target.value, classId: '' }))}>
                <option value="">All</option>{types.map(t => <option key={t.id} value={t.id}>{t.typeName}</option>)}
              </select>
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Report class</label>
              <select className="form-input" style={sel} value={draft.classId} onChange={e => setDraft(d => ({ ...d, classId: e.target.value }))}>
                <option value="">All</option>
                {classes.filter(c => !draft.typeId || c.accountTypeId === draft.typeId).map(c => <option key={c.id} value={c.id}>{c.className}</option>)}
              </select>
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Show</label>
              <select className="form-input" style={sel} value={draft.allCountries ? 'ALL' : ''} onChange={e => setDraft(d => ({ ...d, allCountries: e.target.value === 'ALL' }))}>
                <option value="">The selected entity's subtypes</option><option value="ALL">Every country and legal form</option>
              </select>
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Statement</label>
              <select className="form-input" style={sel} value={draft.statement} onChange={e => setDraft(d => ({ ...d, statement: e.target.value }))}>
                <option value="">All</option>{statements.map(s => <option key={s} value={s}>{statementLabel(s)}</option>)}
              </select>
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={() => { const none = { typeId: '', classId: '', statement: '', allCountries: false }; setDraft(none); setApplied(none); }}>Clear</button>
            <button className="btn btn-primary" onClick={() => { setApplied(draft); setFilterOpen(false); }}>Apply</button>
          </div>
        </div>
      )}
    >
      <div className="data-grid-container">
        {viewMode === 'list' && (
          <SyutGrid data={shown} columns={columns} visibleColumns={['actions', ...visibleColumns.filter(c => c !== 'actions')]}
            enablePagination defaultPageSize={50} rowActions={rowActions}
            emptyMessage={loading ? 'Loading...' : 'No subtypes match.'} />
        )}
        {viewMode === 'grid' && (
          <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '1rem', flex: 1, overflow: 'auto', alignContent: 'start' }}>
              {shown.map(r => (
                <div key={r.id} style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.75rem', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{r.subtypeCode}</div>
                      <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0.2rem 0 0', cursor: 'pointer' }} onClick={() => setViewing(r)}>{r.subtypeName}</h3>
                    </div>
                    <ActionMenu items={rowActions(r)} />
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-primary)' }}>{r.accountTypeName} · {r.reportClassName || '—'}</div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{statementLabel(r.reportType)} · {balance(r.normalBalance)}</div>
                </div>
              ))}
              {shown.length === 0 && <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{loading ? 'Loading...' : 'No subtypes match.'}</div>}
            </div>
          </div>
        )}
      </div>

      {viewing && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-container modal-md" style={{ minHeight: 'auto' }}>
            <div className="modal-header" style={{ minHeight: 'auto' }}>
              <h2 className="modal-title">{viewing.subtypeName}</h2>
              <button className="modal-close" onClick={() => setViewing(null)}><IconSystem.x size={20} /></button>
            </div>
            <div className="modal-content" style={{ color: 'var(--text-primary)' }}>
              <DetailList rows={[
                ['Code', viewing.subtypeCode],
                ['Account type', viewing.accountTypeName],
                ['Report class', viewing.reportClassName || '—'],
                ['Statement', statementLabel(viewing.reportType)],
                ['Section', humanize(viewing.reportSection) || '—'],
                ['Normal balance', balance(viewing.normalBalance)],
                ['Applies to', appliesTo(viewing.countryCodes, viewing.legalForms)],
                ['Description', viewing.description || '—'],
                ['System', viewing.isSystem ? 'Yes (product reference data)' : 'No'],
              ]} />
            </div>
            <div className="modal-footer" style={{ minHeight: 'auto' }}>
              <button className="btn btn-primary" onClick={() => setViewing(null)}>Close</button>
            </div>
          </div>
        </>
      )}
    </EnterpriseScreenShell>
  );
};

export default AccountSubtypesScreen;
